import { useMemo, useState } from 'react';
import { Button, Chip, Input } from '@coordio/ui';
import { memoryModuleService } from '../../services/memoryModuleService';
import { LifecycleStatus, type AgentMemoryDefinitionDto, type InjectionAuditDto, type MemoryContextResponseDto } from '../../types/memoryModule';

interface Trial {
  id: number;
  payorLabel: string;
  declaredScope: Record<string, string>;
  result?: MemoryContextResponseDto;
  audit?: InjectionAuditDto[];
  error?: string;
}

const extractError = (err: unknown): string => {
  const data = (err as { response?: { data?: { message?: string; errors?: string[] } } })?.response?.data;
  if (data?.errors?.length) return data.errors.join(' · ');
  return data?.message || (err as { message?: string })?.message || 'Preview failed';
};

/**
 * Admin-side scoped-injection check. Calls POST /v1/memory/definitions/{id}/preview — never the
 * harness runtime route. Keep the last few trials so AETNA vs CIGNA can sit side by side.
 */
export const MemoryInjectionPreview = ({ definition }: { definition: AgentMemoryDefinitionDto }) => {
  const configurableSlots = useMemo(
    () => definition.scopeSlots.filter((s) => s.ordinal < 5 && s.dimensionKey),
    [definition.scopeSlots]
  );

  const [scope, setScope] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('What is the timely filing limit?');
  const [busy, setBusy] = useState(false);
  const [trials, setTrials] = useState<Trial[]>([]);

  const hasPayor = configurableSlots.some((s) => s.dimensionKey === 'Payor');
  const isActiveCurrent = definition.status === LifecycleStatus.Active && definition.isCurrent;
  const missingRequired = configurableSlots
    .filter((s) => s.required && !scope[s.dimensionKey!]?.trim())
    .map((s) => s.dimensionKey!);

  const runPreview = async (override?: Record<string, string>, replace = false) => {
    const merged = replace ? (override ?? {}) : { ...scope, ...override };
    const declaredScope = Object.fromEntries(
      Object.entries(merged)
        .filter(([, v]) => v.trim() !== '')
        .map(([k, v]) => [k, v.trim()])
    );
    const payorLabel = declaredScope.Payor || Object.values(declaredScope).join(', ') || '(empty scope)';

    setBusy(true);
    try {
      const result = await memoryModuleService.previewContext(definition.agentMemoryDefinitionId, {
        declaredScope,
        query,
        turnIndex: 0,
      });
      let audit: InjectionAuditDto[] | undefined;
      if (result.contextId) {
        try {
          audit = await memoryModuleService.getPreviewAudit(
            definition.agentMemoryDefinitionId,
            result.contextId
          );
        } catch {
          audit = undefined;
        }
      }
      setTrials((prev) =>
        [{ id: Date.now(), payorLabel, declaredScope, result, audit }, ...prev].slice(0, 3)
      );
    } catch (err) {
      setTrials((prev) =>
        [{ id: Date.now(), payorLabel, declaredScope, error: extractError(err) }, ...prev].slice(0, 3)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {!isActiveCurrent && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
          The selected version is not Active and current. Preview always resolves the current
          Active version of this definition — a Draft-only definition returns nothing.
        </div>
      )}

      <div className="rounded-lg border border-border p-8">
        <h6 className="mb-1 text-base font-bold">
          Try injection
        </h6>
        <p className="mb-6 text-sm text-muted-foreground">
          Same retrieval the agent uses, run under your admin login. Fill the declared scope, then
          try Payor = AETNA (rule present) and Payor = CIGNA (rule absent). Chat cannot do this yet
          — sessions do not pass declared scope.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          {configurableSlots.map((slot) => (
            <div key={slot.dimensionKey}>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                {slot.dimensionKey}{slot.required && ' *'}
              </label>
              <Input
                className="w-full"
                required={slot.required}
                value={scope[slot.dimensionKey!] ?? ''}
                onChange={(e) =>
                  setScope((s) => ({ ...s, [slot.dimensionKey!]: e.target.value }))
                }
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {slot.required ? 'Required on this definition' : 'Optional'}
              </p>
            </div>
          ))}
          <div className="col-span-full">
            <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">Query</label>
            <Input
              className="w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {hasPayor && (
            <>
              <Button
                disabled={busy}
                onClick={() => runPreview({ Payor: 'AETNA' })}
              >
                Try Payor = AETNA
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => runPreview({ Payor: 'CIGNA' })}
              >
                Try Payor = CIGNA
              </Button>
            </>
          )}
          <Button variant="outline" disabled={busy} onClick={() => runPreview()}>
            Try with values above
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => runPreview({ Payer: 'AETNA' }, true)}
          >
            Try typo &quot;Payer&quot;
          </Button>
        </div>
        {missingRequired.length > 0 && hasPayor === false && (
          <p className="mt-3 text-xs text-muted-foreground">
            Required slots still empty: {missingRequired.join(', ')}. Missing required scope fails
            closed (nothing injected).
          </p>
        )}
      </div>

      {trials.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No trials yet. Run AETNA, then CIGNA — both results stay on this page so you can compare.
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {trials.map((trial) => (
          <div key={trial.id} className={trials.length === 1 ? 'md:col-span-2' : ''}>
            <TrialCard trial={trial} />
          </div>
        ))}
      </div>
    </div>
  );
};

const TrialCard = ({ trial }: { trial: Trial }) => {
  const injected = trial.result?.injected ?? [];
  const instructions = trial.result?.instructions ?? '';
  const hasRule = injected.some((i) => i.type === 'procedural') && instructions.length > 0;
  const scopeFail = trial.audit?.some((a) => (a.errorCode ?? '').startsWith('ScopeCheck'));
  const scopeLabel = Object.entries(trial.declaredScope)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ') || 'empty';

  return (
    <div className="h-full rounded-lg border border-border p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold">
          {trial.payorLabel}
        </span>
        <Chip label={scopeLabel} variant="outlined" color="default" />
        {trial.error && <Chip label="Request failed" color="red" variant="filled" />}
        {!trial.error && hasRule && <Chip label="Rule injected" color="green" variant="filled" />}
        {!trial.error && !hasRule && (
          <Chip label="Nothing injected" color={scopeFail ? 'amber' : 'default'} variant="filled" />
        )}
      </div>

      {trial.error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-300">
          {trial.error}
        </div>
      )}

      {trial.result && (
        <>
          <Row label="Degradation" value={trial.result.degradation} />
          <Row label="Context id" value={trial.result.contextId || '—'} mono />
          <Row
            label="Injected"
            value={
              injected.length === 0
                ? 'none'
                : injected.map((i) => `${i.type}${i.ruleFamilyId ? ` ${i.ruleFamilyId.slice(0, 8)}…` : ''}`).join(', ')
            }
          />
          <p className="mt-4 mb-1 text-xs text-muted-foreground">
            Instructions (procedural — must hold the rule, never a user message)
          </p>
          <div
            className="min-h-[72px] whitespace-pre-wrap rounded bg-muted p-4 font-mono text-[13px]"
          >
            {instructions || '(empty — no procedural memory)'}
          </div>
          <p className="mt-4 mb-1 text-xs text-muted-foreground">
            Messages (episodic — must stay empty until Phase 3)
          </p>
          <p className="text-sm">
            {trial.result.messages.length === 0
              ? '(empty)'
              : trial.result.messages.map((m) => m.content).join('\n')}
          </p>
        </>
      )}

      {trial.audit && trial.audit.length > 0 && (
        <div className="mt-4">
          <span className="text-xs text-muted-foreground">
            Audit
          </span>
          {trial.audit.map((row) => (
            <p key={row.injectionAuditId} className="text-sm">
              {row.verdict}
              {row.errorCode ? ` · ${row.errorCode}` : ''}
              {row.ruleFamilyId ? ` · rule ${row.ruleFamilyId.slice(0, 8)}…` : ''}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex gap-4 py-1">
    <span className="min-w-[88px] text-xs text-muted-foreground">
      {label}
    </span>
    <span
      className={`text-sm ${mono ? 'font-mono text-xs' : ''}`}
    >
      {value}
    </span>
  </div>
);
